import React from 'react';
import {
  ArrowRight,
  Shield,
  Clock,
  CreditCard,
  Ban,
  RefreshCw,
  Layers,
  TrendingUp,
  ClipboardList,
  Sparkles,
  CheckCircle2,
  XCircle,
  Target,
  FileText,
  Zap,
  AlertTriangle,
  Building2,
  Users,
  Briefcase,
  DollarSign,
  Settings,
  HelpCircle
} from 'lucide-react';
import Logo from '../Logo';
import { SEO } from '../SEO';
import { Button } from '../ui/Button';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'wouter';
import { SupportFooter } from '../SupportFooter';

const problemPoints = [
  {
    icon: Building2,
    title: "Small businesses are not all the same",
    description: "A local service company has completely different needs than an online store or a consulting firm."
  },
  {
    icon: HelpCircle,
    title: "Software advice online is generic and conflicting",
    description: "One article says Slack, another says Teams, another says neither. You're more confused than when you started."
  },
  {
    icon: AlertTriangle,
    title: "Choosing wrong leads to wasted money",
    description: "Lock into the wrong platform and you're stuck with costly migrations and lost productivity down the road."
  },
  {
    icon: Layers,
    title: "Most founders overbuy or miss essentials",
    description: "You either end up with 10 tools you barely use, or realize too late you're missing something critical."
  }
];

const situationFactors = [
  {
    icon: Building2,
    label: "Business type",
    detail: "Service, online, local, retail, consulting"
  },
  {
    icon: Users,
    label: "Team size",
    detail: "Solo, small team, or growing staff"
  },
  {
    icon: Settings,
    label: "Technical comfort",
    detail: "DIY-friendly or needs simplicity"
  },
  {
    icon: DollarSign,
    label: "Budget constraints",
    detail: "Lean startup or room to invest"
  },
  {
    icon: Target,
    label: "Current goals",
    detail: "Sales, operations, admin, growth"
  }
];

const blueprintFeatures = [
  {
    icon: Target,
    title: "Core tools you actually need right now",
    description: "Not a bloated list — just what fits your stage"
  },
  {
    icon: XCircle,
    title: "Tools to avoid at your stage",
    description: "Skip expensive software you'll never fully use"
  },
  {
    icon: TrendingUp,
    title: "When to add more software as you grow",
    description: "Clear triggers for scaling your stack"
  },
  {
    icon: FileText,
    title: "Plain-English explanations",
    description: "No jargon — just clear reasoning you can trust"
  }
];

const steps = [
  {
    icon: ClipboardList,
    title: "Answer a few questions about your business",
    description: "What you do, your team, budget, and goals"
  },
  {
    icon: Sparkles,
    title: "We analyze your needs across dozens of categories",
    description: "Matched against 100+ business tools"
  },
  {
    icon: CheckCircle2,
    title: "Get a clear, personalized software blueprint",
    description: "Know exactly what to use and what to skip"
  }
];

const faqItems = [
  {
    question: "What software should a small business use?",
    answer: "The right software depends on your business type, team size, budget, and goals. btoolme creates a personalized blueprint that tells you exactly what tools fit your situation."
  },
  {
    question: "How is this different from 'best software' lists?",
    answer: "Generic lists recommend the same tools to everyone. btoolme analyzes your specific situation and recommends only what you actually need."
  },
  {
    question: "How long does it take?",
    answer: "About 3 minutes to complete the questionnaire, then instant access to your full Business Software Blueprint."
  },
  {
    question: "What if the recommendations don't help me?",
    answer: "If you don't feel confident in your software decisions after using btoolme, we'll refund you — no questions asked."
  }
];

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Choose the Right Software for Your Small Business",
  "description": "Get a personalized, situation-specific software blueprint for your small business in minutes",
  "totalTime": "PT3M",
  "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "29" },
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Describe Your Business Situation",
      "text": "Answer questions about your business type, team size, technical comfort level, budget, and current goals — takes about 90 seconds.",
      "url": "https://btoolme.com/questionnaire",
      "itemListElement": [
        { "@type": "HowToTip", "text": "Small businesses are not all the same — a service company has completely different software needs than an online store." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "We Match You to the Right Tools",
      "text": "Your profile is matched against 100+ business software tools across 36 categories, filtered specifically for small business needs and your stage.",
      "itemListElement": [
        { "@type": "HowToTip", "text": "No generic lists — the tools you see are ranked by fit for your specific situation, not by popularity." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Receive Your Business Software Blueprint",
      "text": "Get a complete, personalized plan: the tools you need now, tools to add as you grow, what to avoid, and why each recommendation fits your business.",
      "url": "https://btoolme.com/unlock"
    }
  ]
};

export function SmallBusinessSoftwareLandingPage() {
  const [_, navigate] = useLocation();

  const seoTitle = "What Software Should a Small Business Use? | btoolme";
  const seoDescription = "There's no universal 'best software' for small businesses. btoolme builds a personalized software blueprint based on your business type, budget, and goals — so you choose with confidence.";

  const guaranteeText = "If you don't feel confident in your software decisions after using btoolme, we'll refund you — no questions asked.";

  return (
    <div className="min-h-screen flex flex-col" id="main-content">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/what-software-should-a-small-business-use"
        faqItems={faqItems}
        keywords="what software should a small business use, small business software, best software for small business, small business tools, business software recommendations, software for startups, small business software stack, essential business software, business software guide, software for new business, small business technology, small company software, SMB software, business software bundle"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-gray-50/50 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-slate-200/30 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-gray-100/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-100/40 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <Logo className="mb-8 mx-auto" />

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              What Software Should a Small Business Use?
            </h1>

            <p className="text-xl sm:text-2xl text-gray-700 mb-6 font-medium max-w-3xl mx-auto">
              There's no universal "best software." The right tools depend on your business type, budget, and stage.
            </p>

            <div className="flex flex-col gap-2 text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              <p>Online advice is generic. Most founders overbuy or miss essentials.</p>
              <p className="font-medium text-gray-700">btoolme builds a personalized blueprint: what to use, what to skip, and when.</p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-8 py-4 text-lg sparkle-button w-full sm:w-auto"
                size="lg"
                aria-label="Build your personalized software blueprint"
              >
                Build My Software Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">
                  Satisfaction Guaranteed
                </span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" />
                <span>3-minute questionnaire</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-600" />
                <span>One-time payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-slate-600" />
                <span>No subscriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Why This Question Is Harder Than It Should Be
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choosing business software should be straightforward. Here's why it often isn't.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {problemPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reframe Section */}
      <section className="py-16 sm:py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              The Right Software Depends on Your Situation
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              What works for one small business won't work for another. Your software decisions should be based on:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {situationFactors.slice(0, 3).map((factor, index) => {
              const Icon = factor.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{factor.label}</p>
                    <p className="text-sm text-gray-500">{factor.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10">
            {situationFactors.slice(3).map((factor, index) => {
              const Icon = factor.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{factor.label}</p>
                    <p className="text-sm text-gray-500">{factor.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl mx-auto text-center">
            <p className="text-slate-800 font-medium">
              That's why static lists and "best software" articles don't work. They're not built for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How btoolme Helps Small Businesses Choose the Right Software
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              btoolme creates a Business Software Blueprint — a personalized plan built from a short questionnaire about your specific situation. No tool overload, just clarity.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {blueprintFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative flex flex-col items-center text-center">
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-slate-300 to-transparent" />
                  )}

                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/20">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-slate-900 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-gray-900 max-w-[220px]">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-gray-500 text-sm">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-slate-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Stop Guessing. Choose Your Software With Confidence.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get your personalized Business Software Blueprint and know exactly what your small business needs.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={() => navigate('/questionnaire')}
              className="group px-10 py-5 text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
              size="lg"
            >
              Build My Software Blueprint
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <p className="text-sm text-gray-400 max-w-md text-center mt-2">
              {guaranteeText}
            </p>
          </div>

          <p className="mt-10 text-sm text-gray-500">
            Questions? <a href="mailto:btoolmepro@gmail.com" className="text-gray-400 hover:text-white underline">Contact us</a> — we're happy to help.
          </p>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-10 bg-slate-900 border-t border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4 text-center">Related Guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a href="/tools" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Browse All 100+ Tools</p>
              <p className="text-xs text-slate-400">Full curated business software directory</p>
            </a>
            <a href="/tools-needed-to-start-a-business" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Starting a Business?</p>
              <p className="text-xs text-slate-400">Essential tools to launch without overbuying</p>
            </a>
            <a href="/for-trades" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Software for Trades</p>
              <p className="text-xs text-slate-400">Contractors, plumbers, electricians & more</p>
            </a>
          </div>
        </div>
      </section>

      <SupportFooter variant="dark" className="bg-slate-900" />
    </div>
  );
}
